/*
 * Attachment component
 */

import React from 'react';

import Base from './base.jsx'; 
import {
  Mode,
  castDataType,
  DataType,
} from '../common.jsx';
import Helpers from './helpers.jsx'
import Setting from "./setting.jsx";

import { FormContext } from "../form_context.jsx";

import SlideShow from '../../common/SlideShow.jsx';
import { Dashboard } from "@uppy/react";
import uploadManager from '../../../helpers/UploadManager.js';

import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'

import '@uppy/drag-drop/dist/style.css'
import '@uppy/file-input/dist/style.css'
import '@uppy/progress-bar/dist/style.css'

export default class Image extends Base {
    static contextType = FormContext;

    constructor(props, context) {
        super(props);
        this.state = {
            pendingUploads: [],
            uppy: null,
            error: null
        };
        this.uppyId = null;
        this._isMounted = false;
        this.fileSubscriptions = new Map(); // Store subscriptions per file
        this.isLocked =  this.props.content && this.isReadOnlyAnswer();
    }

    static get CATEGORY() {
        return 'other';
    }

    static get TYPE() {
        return 'image';
    }

    static icon(){
        return <i className="fa fa-image"></i>;
    }

    static get SCALE_TYPES() {
        return ['contain','cover'].map(scale => [scale, I18n.t(`scale_${scale}`, {scope: 'fe'})])
    }

    static get HEIGHT_UNITS() {
        return ['vh', 'px']
    }

    static get FORMAT_EXTENSIONS() {
        return ['png', 'jpeg']
    }

    static get FILE_SIZE_UNITS() {
        return { tb: "TB", gb: "GB", mb: "MB", kb: "KB", b: "b" };
    } 

    static get TRANSLATED_ATTRIBUTES() {
        return {
            label: 'component_title_image',
            description: 'field_default_description',
            help: null
        };
    }

    static defaultContent(language) {
        return Object.assign(super.defaultContent(language), {
            label: 'label',
            description: 'description',
            name: 'field_' + Helpers.randomString(),
            help: 'help',
            required: false,
            label_hidden: false,
            height: '10',
            heightUnit: 'vh',
            fileFormats: this.FORMAT_EXTENSIONS,
            reportKey: '',
            scaleType: 'contain',
            maxFiles: 1,
            prettyView: {
                hide: false,
                show_label: false
            }
        });
    }

    renderView() {
        const {uppy} = this.state;
        const {required, label_hidden, scaleType, height, heightUnit, fileFormats} = this.props.content;
        const files = JSON.parse(JSON.stringify(this.getAnswer()))
        let imageLinks = files.slice().sort((f1,f2)=>{return f1.mainImage === "true" ? -1 : f2.mainImage === "true"}).map(f => f.path);

        return (
            // Relative position for the counter
            <div style={{ position: "relative"}}>
                <label className={label_hidden ? 'hidden-label': ''}>
                    <span className="field-label">{this.getText('label')}</span>
                    {required ? ' *' : null}
                    {this.helpIcon()}
                </label>

                {this.renderFileCounter()}
                {
                  [Mode.FILL, Mode.EDIT].includes(this.props.mode) && uppy && <div>
                      <Dashboard
                        id={this.uppyId}
                        name={this.fullFieldName()}
                        uppy={uppy}
                        inline={true}
                        hideUploadButton={true}
                        doneButtonHandler={null}
                        showProgressDetails={false}
                        proudlyDisplayPoweredByUppy={false}
                        showSelectedFiles={false}
                        disabled={Mode.EDIT === this.props.mode}
                        height={150}
                        width='100%'
                        data-upload-id={this.props.attachmentUploadId}
                        data-upload-field={this.fullFieldName()}
                      />
                      {this.renderHiddenInputs()}
                      {(fileFormats || []).length > 0 &&
                        <p className="description">{I18n.t('accepted_file_formats', {scope: 'fe', formats: this.acceptedFormatList()})}</p>
                      }
                  </div>
                }
                {this.renderImages()}
                {this.props.mode === Mode.VIEW &&
                  <SlideShow images={imageLinks} backgroundSize={`${scaleType}`} height={`${height}${heightUnit}`} />
                }
            </div>
        );
    }

    renderPrettyView() {
        const {scaleType, height, heightUnit} = this.props.content
        let imageLinks = JSON.parse(JSON.stringify(this.getAnswer())).slice().sort((f1,f2)=>{return f1.mainImage === "true" ? -1 : f2.mainImage === "true"}).map(f => f.path)
        const { hide = false, show_label = false } = this.props.content.prettyView || {};

        return ( hide ? null :
            <>
                {show_label &&
                    <label>
                        <span className="field-label">{this.getText('label')}</span>
                    </label>
                }
                <SlideShow images={imageLinks} backgroundSize={`${scaleType}`} height={`${height}${heightUnit}`} />
            </>
        )
    }
    // Render the file objects using hidden inputs. Use the rails naming convetions to keep it as object
    renderHiddenInputs () {
        return <>
        {this.getAnswer().map(file => {
            return Object.keys(file).map(prop => {
            if (typeof file[prop] != 'object') {
                return <input 
                type='hidden' 
                name={`${this.fullFieldName(true)}[${prop}]`} 
                value={file[prop]}
                key={`[${prop}]-${file.id}`}
                />
            }
            // Object
            return Object.keys(file[prop]||[]).map(nestedProp => {
                return <input 
                type='hidden' 
                name={`${this.fullFieldName(true)}[${prop}][${nestedProp}]`} 
                value={file[prop][nestedProp]}
                key={`[${prop}][${nestedProp}]-${file.id}`}
                />
            })
            }
            )
        })}
        </>
    }

    renderImages() {
        const images = this.getUniqueFilesWithPendingUploads();
        if (this.props.mode === Mode.VIEW) {
            return <>{images.length === 0 && I18n.t('no_images_uploaded')}</>
        }
        // visible with permission needed
        return images.map((f) => {
            const {size, unit} = this.getFileSize(f.metadata.size);

            if (Object.values(uploadManager.SCAN_STATUSES).includes(f.status)) {
                return (
                   <div key={f.id} className='d-flex' style={{alignItems: 'baseline'}}>
                       <div className={`uppy-preview uppy-file-preview`} style={{marginLeft: '31px'}}>
                          <div className="uppy-details">
                              <span className="uppy-filename">
                                  {f.metadata.filename}
                              </span>
                              <a href="#" className="btn btn-danger" data-dz-remove onClick={(e) => this.deleteFile(e, f.attachment_id)}>
                                  <i className="icon-trash"/>
                              </a>
                          </div>
                          <span>{this.renderScanStatus(f.status)}</span>
                       </div>
                   </div>
                )
            }

            const name = <a
                        href={f.path}
                        data-turbo="false"
                        data-av="true"
                        data-av-content-type={f.metadata.mime_type}
                        data-av-show-download-link={f.path}
                        data-av-title={f.metadata.filename}
                    >
                        {f.metadata.filename}
                    </a>
            return (
                <div key={f.id} className='d-flex' style={{alignItems: 'baseline'}} >
                    <input type="radio" name={`mainImage-${this.fullFieldName()}`} onChange={e=>this.updateMainImage(e)} value={f.id} checked={f.mainImage === "true"} className=' mr-2' style={{width: 'auto'}}/>
                    <div className={`uppy-preview uppy-file-preview`} >
                        <div className="uppy-details">
                            <span className="uppy-filename">
                                {name}
                                {f.metadata.size ? (
                                <span className="uppy-size ml-1" data-uppy-size>
                                    <strong>{size}</strong> {unit}
                                </span>
                            ) : null}
                            </span>
                            {!this.isLocked && (
                                <a href="#" className="btn btn-danger" data-dz-remove onClick={(e) => this.deleteFile(e, f.attachment_id)}>
                                    <i className="icon-trash"></i>
                                </a>
                            )}
                        </div>
                    </div>
                </div>

            );
        });
    }

    renderFileCounter() {
        const files = this.getUniqueFilesWithPendingUploads();
        const maxFiles = this.getMaxFiles();
        return maxFiles && this.props.mode != Mode.VIEW && !this.isLocked ? (
            <span className="attachment-counter-count">
                <span className={`label label-${files.length < maxFiles ? "default" : "info"}`}>
                    <span className="current">{files.length}</span> / <span className="limit">{maxFiles}</span> {I18n.t("files", { scope: "fe" }).toLowerCase()}
                </span>
            </span>
        ) : null;
    }

    renderSettings() {
        return (
            <div>
                <Setting.Checkbox name="required" label={I18n.t('field_required', {scope: 'fe'})} value={this.props.content.required} onChange={this.updateSettings} />

                <div className='setting'>
                    <label>{I18n.t('height', {scope: 'fe'})}</label>
                    <div className='d-flex'>
                        <input name='height' type='number' value={this.props.content.height} onChange={this.updateSettings} autoComplete={this.getAutoComplete()} />
                        <select name='heightUnit' className="form-control" value={this.props.content.heightUnit} onChange={this.updateSettings} autoComplete={this.getAutoComplete()}>
                            {Image.HEIGHT_UNITS.map(unit =>{
                                return (<option key={unit} value={unit}>{I18n.t(`height_unit_${unit}`, {scope: 'fe'})}</option>)
                            })}
                        </select>  
                    </div>
                </div>

                <Setting.Dropdown name='scale' label={I18n.t('image_scale', {scope: 'fe'})} options={Image.SCALE_TYPES} value={this.props.content.scaleType} description={I18n.t('scale_type_description', {scope: 'fe'})} />
                <Setting.TextField name="maxFiles" label={I18n.t('max_files', {scope: 'fe'})} value={this.getMaxFiles()} onChange={this.updateSettings} description={I18n.t('field_max_files_description', {scope: 'fe'})} />
                {this.renderPrettyViewSettings()}
            </div>
        );
    }

    async componentDidMount() {
        super.componentDidMount();

        const isNotPreviewModal = !$('.form-preview-modal').length;
        if (this.props.mode === Mode.FILL && !this.isLocked && isNotPreviewModal) {
            this._isMounted = true;

            await this.restorePendingUploads();
            if (!this._isMounted) return;

            this.uppyId = uploadManager.getUppyId(this.props.attachmentUploadId, this.fullFieldName());
            await this.initUppy();
        } else {
            const UppyModule = await import('@uppy/core');
            const uppy = new UppyModule.default({
                id: `uppy-view`,
                endpoint: '/files',     // path to tus server
                chunkSize: 2*1024*1024, // 2MB chunks
                removeFingerprintOnSuccess: true,
            })
            this.setState({uppy: uppy});
            this.uppyId = uploadManager.getUppyId(this.props.attachmentUploadId, this.props.content?.name);
        }
    }

    componentDidUpdate() {
        super.componentDidUpdate();

        if (!this._isMounted) return;
        if (typeof this.props.setDisableSubmit == "function") {
            const isUploadInProgress = uploadManager.getActiveUploads(this.props.attachmentUploadId).length > 0;
            this.props.setDisableSubmit(this.fullFieldName(), isUploadInProgress);

            if (this.state.uppy) {
                const maxNumberOfFiles = this.getUniqueFilesWithPendingUploads().length;
                this.state.uppy.getPlugin(this.uppyId)?.setOptions({disabled: maxNumberOfFiles >= this.getMaxFiles()});
            }
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        if (this.uppyId) {
            uploadManager.uppyInstances.delete(this.uppyId);
        }

        // Cleanup all file subscriptions
        this.fileSubscriptions.forEach(unsubscribe => unsubscribe());
        this.fileSubscriptions.clear();

        // Cleanup listener to avoid duplicates after remount
        this.state.uppy?.off('done', this.handleDone);
    }

    beforeUpdateSettings(content) {
        content.required = castDataType(content.required, DataType.BOOLEAN);
        content.label_hidden = castDataType(content.label_hidden, DataType.BOOLEAN);
        content.maxFiles = castDataType(content.maxFiles, DataType.INTEGER) || 1;
        this.beforeUpdateSettingsPrettyView(content);
    }

    updateMainImage = (e) => {
        const id = e.target.value
        const files = JSON.parse(JSON.stringify(this.getAnswer()))
        const updatedMainFile = files.map(f => {
            if (f.id === id){
                f.mainImage = "true";
            } else {
                f.mainImage = "false"
            }
            return f
        })
        this.updateAnswer(updatedMainFile)
    }

    deleteFile = (e, attachment_id) => {
        e.preventDefault();
        let files = JSON.parse(JSON.stringify(this.getAnswer()));
        this.updateAnswer(files.filter(v => v.attachment_id !== attachment_id));
        this.removeFileFromState(attachment_id);
    }

    uppyError = (file, error, status) => {
        if (status === "login-required") {
            AutoSave.updateSaveStatus("session_error");
        }

        if (status && status !== 200) {
            window.alert(I18n.t('upload_image_fail_message',{ scope: 'fe', name: file.name, error: error }));
        }
    }

    acceptedFormatList() {
        return this.props.content.fileFormats.map(f => {
            return I18n.t('file_format_' + f.toLowerCase(), { scope: 'fe' });
        }).join(', ');
    }

    getMaxFiles() {
        return this.props.content.maxFiles === void 0 ? 1 : this.props.content.maxFiles;
    }

    getAllowedTypes() {
        return (this.props.content.fileFormats || []).map(f => `.${f}`)
    }

    getFileSize(size) {
        let selectedSize = 0;
        let selectedUnit = "b";

        if (size > 0) {
            let units = ["tb", "gb", "mb", "kb", "b"];

            for (let i = 0; i < units.length; i++) {
                let unit = units[i];
                let cutoff = Math.pow(1000, 4 - i) / 10;

                if (size >= cutoff) {
                    selectedSize = size / Math.pow(1000, 4 - i);
                    selectedUnit = unit;
                    break;
                }
            }

            selectedSize = Math.round(10 * selectedSize) / 10; // Cutting of digits
        }

        return { size: selectedSize, unit: this.constructor.FILE_SIZE_UNITS[selectedUnit] };
    }

    // rawAnswer stored as string
    rawAnswerDefault() {
        return [];
    }

    restorePendingUploads = async () => {
        const files = JSON.parse(JSON.stringify(this.getAnswer()));
        const pendingUploads = await uploadManager.getPendingUploads(this.props.attachmentUploadId, this.fullFieldName());
        const uploads = pendingUploads.filter(v => Object.values(uploadManager.SCAN_STATUSES).includes(v.status));
        this.setState({pendingUploads: uploads});

        const completedUploads = pendingUploads.filter(v => v.status === 'done');
        if (completedUploads.length > 0) {
            let answers = [...new Map([...files, ...completedUploads].filter(f => f?.id).map(f => [f.id, f])).values()];
            this.updateAnswer(answers);
        }
    }

    initUppy = async () => {
        if (!this._isMounted) return;

        const maxNumberOfFiles = this.getMaxFiles() - this.getAnswer().length;
        const allowedTypes = this.getAllowedTypes();

        const uppy = await uploadManager.getUppyInstance(this.uppyId, {
            multiple: false,
            maxNumberOfFiles: maxNumberOfFiles,
            allowedTypes: allowedTypes,
            onFileAdded: async (file) => {
                const {token} = await uploadManager.getOrCreateTokenForFile(file, this.props.attachmentUploadId, this.fullFieldName());
                file.meta = {...file.meta, token};

                // Subscribe to upload updates and store unsubscribe function for cleanup
                const unsubscribe = uploadManager.onUpdate(token, this.uppyId, ({status, attachment}) => {
                    if (!this._isMounted) return;

                    if (status === 'done') {
                        this.updateAnswer([...this.getAnswer(), (({status, scan_id, ...rest}) => rest)(attachment)]);
                    }
                    this.setState(
                      (prev) => ({
                          pendingUploads: prev.pendingUploads.some(f => f.token === token)
                            ? prev.pendingUploads.map(f =>
                              f.token === token
                                ? { ...f, ...attachment, token, status }
                                : f
                            )
                            : [...prev.pendingUploads, { ...attachment, token, status }]
                      }),
                      () => {
                          this.updateUppyRestrictions();
                      }
                    );
                });

                // Store unsubscribe for cleanup
                this.fileSubscriptions.set(token, unsubscribe)
                return file;
            },
            onUploadSuccess: async (file, response) => {
                const permissions = this.props.imagePermissions || {};
                const result = await uploadManager.finalizeUpload(file, response, permissions);
                if (!result.ok) {
                    this.uppyError(file, response.statusText, response.status)
                }
            }
        });

        // Attach done listener with proper context binding
        this.handleDone = this.handleDone.bind(this);
        uppy.on('done', this.handleDone);

        this.setState({uppy});
    }

    handleDone({attachment}) {
        this.updateAnswer([...this.getAnswer(), (({status, scan_id, ...rest}) => rest)(attachment)]);
    }

    removeFileFromState = (attachment_id) => {
        const file = [...this.state.pendingUploads, ...this.getAnswer()].find(f => f?.attachment_id === attachment_id);
        if (file?.token) {
            uploadManager.cancelUpload(file.token, this.state.uppy)
        }
        this.setState(prev => ({pendingUploads: prev.pendingUploads.filter(f => f.attachment_id !== attachment_id)}));
        this.updateUppyRestrictions();
    }

    updateUppyRestrictions = () => {
        const remaining = this.getMaxFiles() - this.getUniqueFilesWithPendingUploads().length;
        const currentNumberOfFiles = this.state.uppy.getFiles().length;
        this.state.uppy.setOptions({
            restrictions: {
                maxNumberOfFiles: Math.max(1, remaining + currentNumberOfFiles),
            }
        });
    }

    getUniqueFilesWithPendingUploads = () => {
        const files = this.getAnswer() || [];
        const filteredPending = (this.state.pendingUploads || []).filter(v =>
          Object.values(uploadManager.SCAN_STATUSES).includes(v.status)
        );
        return [...files, ...filteredPending].filter(Boolean).filter((item, index, arr) => {
            if (!item?.attachment_id) return false;
            return arr.findIndex(f => f.attachment_id === item.attachment_id) === index;
        });
    }

    renderScanStatus(status) {
        switch (status) {
            case uploadManager.SCAN_STATUSES.SCANNING:
                return <span className="text-info"><i className="fa fa-gear fa-spin"/> Scanning for viruses...</span>;
            case uploadManager.SCAN_STATUSES.SCAN_FAILED:
                return <span className="text-warning"><i className="fa fa-times"/> Virus scan failed. Please reupload the file.</span>;
            case uploadManager.SCAN_STATUSES.INFECTED:
                return <span className="text-danger"><i className="fa fa-virus"/> Virus scan finished. Infected files were found and the upload was rejected.</span>
            default:
                return;
        }
    }
}
